import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Alert,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaIcon from '@mui/icons-material/Spa';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { parse, addDays, setHours, setMinutes, setSeconds, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import { getOrders, updateOrder } from '../../services/orderService';
import api from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessDate } from '../../hooks/useBusinessDate';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { toCastOrderView, extractOrders } from '../cast/castOrderUtils';

interface DriverOption {
  id: number;
  display_name: string;
}

/**
 * ドライバー専用のオーダー一覧画面。
 * 表示はキャスト画面と同様のカード。自分が入室担当または退出担当のオーダーが表示される。
 * 各オーダーで入室担当・退出担当ドライバーを個別に変更できる（引継ぎ）。
 * 選択肢は「入室時刻 / 退出時刻 がそのドライバーのシフト内」のドライバーに限定する。
 */
const DriverOrderList: React.FC = () => {
  const { user } = useAuth();
  const { businessDate, isLoading: isDateLoading, formatBusinessDate, businessHours } = useBusinessDate();
  const { loading: settingsLoading } = useSystemSettings();

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // 担当変更ダイアログ
  const [editOrder, setEditOrder] = useState<any | null>(null);
  const [entryDriverOptions, setEntryDriverOptions] = useState<DriverOption[]>([]);
  const [exitDriverOptions, setExitDriverOptions] = useState<DriverOption[]>([]);
  const [selectedEntryDriver, setSelectedEntryDriver] = useState<DriverOption | null>(null);
  const [selectedExitDriver, setSelectedExitDriver] = useState<DriverOption | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchOrders = useCallback(async (date: Date) => {
    if (!user || !businessHours) return;
    try {
      setLoading(true);
      setError(null);
      const startTime = parse(businessHours.start, 'HH:mm', new Date());
      const endTime = parse(businessHours.end, 'HH:mm', new Date());
      const searchStart = setSeconds(setMinutes(setHours(date, startTime.getHours()), startTime.getMinutes()), 0);
      let searchEnd;
      if (endTime < startTime) {
        const nextDay = addDays(date, 1);
        searchEnd = setSeconds(setMinutes(setHours(nextDay, endTime.getHours()), endTime.getMinutes()), 0);
      } else {
        searchEnd = setSeconds(setMinutes(setHours(date, endTime.getHours()), endTime.getMinutes()), 0);
      }
      // バックエンドが driver ロールで自分担当（入室 or 退出）のオーダーだけ返す
      const response = await getOrders({
        start_date: searchStart.toISOString(),
        end_date: searchEnd.toISOString(),
      });
      setOrders(extractOrders(response));
    } catch (err) {
      console.error('オーダー取得に失敗しました:', err);
      setError('オーダーデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [user, businessHours]);

  useEffect(() => {
    if (businessDate && !selectedDate) setSelectedDate(businessDate);
  }, [businessDate, selectedDate]);

  useEffect(() => {
    const init = async () => {
      const targetDate = selectedDate || businessDate;
      if (user && targetDate && !settingsLoading && !isDateLoading) {
        await fetchOrders(targetDate);
      } else if (!settingsLoading && !isDateLoading) {
        setLoading(false);
      }
    };
    init();
  }, [user, selectedDate, businessDate, settingsLoading, isDateLoading, fetchOrders]);

  const sortedOrders = useMemo(() => {
    const views = orders.map((o: any) => ({ raw: o, view: toCastOrderView(o) }));
    views.sort((a, b) =>
      sortOrder === 'asc'
        ? a.view.reservationSortKey - b.view.reservationSortKey
        : b.view.reservationSortKey - a.view.reservationSortKey
    );
    return views;
  }, [orders, sortOrder]);

  // 入室時刻・退出時刻の ISO(YYYY-MM-DDTHH:MM) を求める
  const getEntryExitDateTimes = (order: any): { entryDt: Date | null; exitDt: Date | null } => {
    // 入室の基準時刻：entry_time(ISO) があればそれ、無ければ予約日時
    let entryDt: Date | null = null;
    if (order?.entry_time) {
      try { entryDt = parseISO(order.entry_time); } catch { entryDt = null; }
    }
    if (!entryDt && order?.reservation_datetime) {
      try { entryDt = parseISO(order.reservation_datetime); } catch { entryDt = null; }
    }
    // 退出時刻 = 入室 + コース + サービスタイム
    let exitDt: Date | null = null;
    const courseMinutes = Number(order?.course_duration) || 0;
    const serviceMinutes = Number(order?.service_time) || 0;
    if (entryDt && courseMinutes > 0) {
      exitDt = new Date(entryDt.getTime() + (courseMinutes + serviceMinutes) * 60 * 1000);
    }
    return { entryDt, exitDt };
  };

  const toLocalDtParam = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fetchDriversForDatetime = async (dt: Date | null): Promise<DriverOption[]> => {
    if (!dt) return [];
    try {
      const res = await api.get('/users_edit/available', {
        params: { datetime: toLocalDtParam(dt), role: 'DRIVER' },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      return list.map((u: any) => ({
        id: u.id,
        display_name: u.display_name || `${u.last_name || ''} ${u.first_name || ''}`.trim() || u.username,
      }));
    } catch (err) {
      console.error('シフト内ドライバーの取得に失敗:', err);
      return [];
    }
  };

  const openEditDialog = async (order: any) => {
    setEditOrder(order);
    setDialogLoading(true);
    setSelectedEntryDriver(null);
    setSelectedExitDriver(null);
    try {
      const { entryDt, exitDt } = getEntryExitDateTimes(order);
      const [entryOpts, exitOpts] = await Promise.all([
        fetchDriversForDatetime(entryDt),
        fetchDriversForDatetime(exitDt),
      ]);
      setEntryDriverOptions(entryOpts);
      setExitDriverOptions(exitOpts);
      // 現在の担当を初期選択に反映（候補に無くても表示できるよう補完）
      const curEntry = order.entry_driver_id || order.driver_id;
      const curExit = order.exit_driver_id;
      if (curEntry) {
        const found = entryOpts.find((o) => o.id === curEntry) ||
          (order.entry_driver ? { id: curEntry, display_name: order.entry_driver.name } : null);
        setSelectedEntryDriver(found || null);
      }
      if (curExit) {
        const found = exitOpts.find((o) => o.id === curExit) ||
          (order.exit_driver ? { id: curExit, display_name: order.exit_driver.name } : null);
        setSelectedExitDriver(found || null);
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      await updateOrder(editOrder.id, {
        entry_driver_id: selectedEntryDriver?.id || null,
        exit_driver_id: selectedExitDriver?.id || null,
      });
      toast.success('担当ドライバーを更新しました');
      setEditOrder(null);
      const targetDate = selectedDate || businessDate;
      if (targetDate) await fetchOrders(targetDate);
    } catch (err: any) {
      console.error('担当ドライバーの更新に失敗:', err);
      toast.error(err?.response?.data?.message || '担当ドライバーの更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 自分が入室/退出のどちらを担当しているか（カード上のバッジ用）
  const myRole = (order: any): string => {
    const myId = user?.id;
    const isEntry = (order.entry_driver_id || order.driver_id) === myId;
    const isExit = order.exit_driver_id === myId;
    if (isEntry && isExit) return '入室・退出担当';
    if (isEntry) return '入室担当';
    if (isExit) return '退出担当';
    return '';
  };

  if (settingsLoading || isDateLoading) {
    return (
      <DashboardLayout title="オーダー一覧">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="オーダー一覧">
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Typography variant="h5" gutterBottom>オーダー一覧</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          営業日（基準）: {formatBusinessDate(businessDate)}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
            <DatePicker
              label="表示する日付"
              value={selectedDate}
              onChange={(newValue) => newValue && setSelectedDate(newValue)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
          <ToggleButtonGroup size="small" exclusive value={sortOrder} onChange={(_e, val) => val && setSortOrder(val)}>
            <ToggleButton value="asc">予約が早い順</ToggleButton>
            <ToggleButton value="desc">予約が遅い順</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sortedOrders.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            選択した日付のオーダーはありません
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {sortedOrders.map(({ raw, view }) => (
              <Card
                key={view.id}
                variant="outlined"
                sx={view.isCompleted ? { borderColor: 'success.main', bgcolor: 'success.50' } : undefined}
              >
                <CardActionArea onClick={() => openEditDialog(raw)}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{view.reservationLabel}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {myRole(raw) && <Chip label={myRole(raw)} size="small" color="info" />}
                        {view.isCompleted && <Chip label="完了" size="small" color="success" />}
                      </Stack>
                    </Stack>

                    {/* コースの横に予約タイプ（指名など）を配置してはみ出しを防ぐ */}
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SpaIcon fontSize="small" color="action" />
                        <Typography variant="body1">{view.courseLabel}</Typography>
                      </Stack>
                      <Chip label={view.orderTypeLabel} size="small" color="primary" variant="outlined" />
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <HotelIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>{view.hotelLabel}</Typography>
                    </Stack>

                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2}>
                      {view.entryTimeLabel && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <LoginIcon fontSize="small" color="success" />
                          <Typography variant="body2">入室 {view.entryTimeLabel}</Typography>
                        </Stack>
                      )}
                      {view.exitTimeLabel && (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <LogoutIcon fontSize="small" color="action" />
                          <Typography variant="body2">退出 {view.exitTimeLabel}</Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
                      <DirectionsCarIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        入室: {raw.entry_driver?.name || '未割当'} / 退出: {raw.exit_driver?.name || '未割当'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}
      </Container>

      {/* 担当ドライバー変更ダイアログ */}
      <Dialog open={!!editOrder} onClose={() => setEditOrder(null)} maxWidth="xs" fullWidth>
        <DialogTitle>担当ドライバーの変更（引継ぎ）</DialogTitle>
        <DialogContent dividers>
          {dialogLoading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                入室時刻・退出時刻がシフト内のドライバーのみ選択できます。
              </Typography>
              <Autocomplete
                options={entryDriverOptions}
                value={selectedEntryDriver}
                onChange={(_, val) => setSelectedEntryDriver(val)}
                getOptionLabel={(o) => o.display_name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="入室担当ドライバー" />}
                noOptionsText="入室時刻にシフト内のドライバーがいません"
              />
              <Autocomplete
                options={exitDriverOptions}
                value={selectedExitDriver}
                onChange={(_, val) => setSelectedExitDriver(val)}
                getOptionLabel={(o) => o.display_name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="退出担当ドライバー" />}
                noOptionsText="退出時刻にシフト内のドライバーがいません"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrder(null)} disabled={saving}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveAssignment} disabled={saving || dialogLoading}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default DriverOrderList;
